import React, {useMemo, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import IconButton from '@mui/material/IconButton';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import SettingsSectionBase, {SettingsSection} from '../../Forms/PrivateFormSettings/SettingsSection';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import {useGetGroupFiltersQuery} from '../../../../api/services/groupService';
import {
    useDeleteStudentProfileAccessRestrictionRuleMutation,
    useGetStudentProfileAccessRestrictionRulesQuery,
    useLazyGetStudentProfileAccessRestrictionRuleQuery,
    useUpsertStudentProfileAccessRestrictionRuleMutation
} from '../../../../api/services/schoolManagementService';
import useNotification from '../../../../utility/hooks/useNotification';
import StudentProfileAccessRestrictionDialog from './StudentProfileAccessRestrictionDialog';

/* eslint-disable react/prop-types */

const StudentProfileAccessRestrictions = () => {
    const {showSuccessNotification, showErrorNotification} = useNotification();
    const {
        data: rules = [],
        isLoading: isLoadingRules
    } = useGetStudentProfileAccessRestrictionRulesQuery();
    const {data: groupFiltersResponse = {modules: []}} = useGetGroupFiltersQuery({});
    const [fetchRule] = useLazyGetStudentProfileAccessRestrictionRuleQuery();
    const [upsertRule, {isLoading: isSaving}] = useUpsertStudentProfileAccessRestrictionRuleMutation();
    const [deleteRule, {isLoading: isDeleting}] = useDeleteStudentProfileAccessRestrictionRuleMutation();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [editingRuleId, setEditingRuleId] = useState(null);
    const [dialogInitialValues, setDialogInitialValues] = useState(null);
    const [isLoadingRule, setIsLoadingRule] = useState(false);
    const [rulePendingDelete, setRulePendingDelete] = useState(null);

    const ruleColumns = useMemo(() => [
        {
            accessorKey: 'subjectLabel',
            header: 'Subject',
            grow: true,
            Cell: ({row}) => (
                <Box>
                    <Typography variant="body2" fontWeight="bold">
                        {row.original.subjectLabel}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {row.original.subjectType === 'employee' ? 'Employee' : 'Employee group'}
                    </Typography>
                </Box>
            )
        },
        {
            accessorKey: 'exclusions',
            header: 'Excluded',
            size: 200,
            Cell: ({row}) => (
                <Typography variant="body2">
                    {row.original.studentCount} profile(s), {row.original.studentGroupCount} group(s)
                </Typography>
            )
        }
    ], []);

    const handleOpenCreate = () => {
        setDialogMode('create');
        setEditingRuleId(null);
        setDialogInitialValues(null);
        setDialogOpen(true);
    };

    const handleOpenEdit = async (ruleId) => {
        setDialogMode('edit');
        setEditingRuleId(ruleId);
        setDialogOpen(true);
        setIsLoadingRule(true);
        setDialogInitialValues(null);

        try {
            const rule = await fetchRule(ruleId).unwrap();
            setDialogInitialValues({
                subjectType: rule.subjectType,
                employee: rule.employeeId
                    ? {
                        id: rule.employeeId,
                        preferredForename: rule.preferredForename,
                        preferredSurname: rule.preferredSurname,
                        displayName: rule.employeeName
                    }
                    : null,
                employeeGroup: rule.employeeGroupFilterId
                    ? {
                        groupFilterId: rule.employeeGroupFilterId,
                        customGroupId: rule.employeeCustomGroupId,
                        name: rule.employeeGroupName
                    }
                    : null,
                students: (rule.students || [])
                    .filter(student => !student.isApplicant)
                    .map(student => ({
                        id: student.id,
                        label: student.label
                    })),
                applications: (rule.students || [])
                    .filter(student => student.isApplicant)
                    .map(student => ({
                        id: student.id,
                        label: student.label
                    })),
                studentGroups: (rule.studentGroups || []).map(group => ({
                    id: group.groupFilterId,
                    groupFilterId: group.groupFilterId,
                    name: group.name
                }))
            });
        } catch (e) {
            showErrorNotification('Failed to load restriction rule');
            setDialogOpen(false);
        } finally {
            setIsLoadingRule(false);
        }
    };

    const handleCloseDialog = () => {
        if (isSaving) {
            return;
        }
        setDialogOpen(false);
        setEditingRuleId(null);
        setDialogInitialValues(null);
    };

    const handleSave = async (payload) => {
        try {
            await upsertRule({
                ruleId: payload.ruleId,
                employeeId: payload.employeeId,
                employeeGroupFilterId: payload.employeeGroupFilterId,
                studentIds: payload.studentIds,
                studentGroupFilterIds: payload.studentGroupFilterIds
            }).unwrap();

            showSuccessNotification(
                dialogMode === 'edit'
                    ? 'Restriction updated'
                    : 'Restriction created'
            );
            handleCloseDialog();
        } catch (e) {
            showErrorNotification(
                e?.data?.detail ||
                e?.data?.title ||
                'Failed to save student profile access restriction'
            );
        }
    };

    const handleConfirmDelete = async () => {
        if (!rulePendingDelete) {
            return;
        }

        try {
            await deleteRule(rulePendingDelete.id).unwrap();
            showSuccessNotification('Restriction removed');
            setRulePendingDelete(null);
            if (editingRuleId === rulePendingDelete.id) {
                handleCloseDialog();
            }
        } catch (e) {
            showErrorNotification('Failed to delete restriction rule');
        }
    };

    return (
        <SettingsSectionBase
            header="Student profile access"
            headerIcon={<LockPersonOutlinedIcon fontSize="small"/>}
            subHeader="Restrict selected employees or employee groups from opening specific students, applications, or groups in detail."
        >
            <SettingsSection sx={{px: 2.75, py: 2.5}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                        {rules.length} restriction rule(s)
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddOutlinedIcon/>}
                        onClick={handleOpenCreate}
                        sx={{
                            backgroundColor: '#186BBE',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#155FA7',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Add restriction
                    </Button>
                </Stack>

                {isLoadingRules
                    ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress size={28}/>
                        </Box>
                    )
                    : (
                        <AdmicityTableV2
                            columns={ruleColumns}
                            data={rules}
                            hideFooterPagination
                            tableProps={{
                                layoutMode: 'grid-no-grow',
                                enableRowActions: true,
                                enableSorting: false,
                                enableFilters: false,
                                enableHiding: false,
                                enableFullScreenToggle: false,
                                enableColumnResizing: false,
                                enableBottomToolbar: false,
                                enableTopToolbar: false,
                                muiTablePaperProps: {
                                    sx: {
                                        flexGrow: 0,
                                    },
                                },
                                muiTableContainerProps: {
                                    sx: {
                                        flex: 'none',
                                    },
                                },
                                renderRowActions: ({row}) => (
                                    <Stack direction="row" spacing={0.5}>
                                        <IconButton
                                            size="small"
                                            aria-label="Edit restriction"
                                            onClick={() => handleOpenEdit(row.original.id)}
                                        >
                                            <EditOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            aria-label="Delete restriction"
                                            onClick={() => setRulePendingDelete(row.original)}
                                            disabled={isDeleting}
                                        >
                                            <DeleteOutlineRoundedIcon fontSize="small"/>
                                        </IconButton>
                                    </Stack>
                                )
                            }}
                        />
                    )}
            </SettingsSection>

            <StudentProfileAccessRestrictionDialog
                open={dialogOpen}
                mode={dialogMode}
                ruleId={editingRuleId}
                initialValues={dialogInitialValues}
                existingRules={rules}
                groupFilterModules={groupFiltersResponse.modules || []}
                isLoading={isLoadingRule}
                isSaving={isSaving}
                onClose={handleCloseDialog}
                onSave={handleSave}
            />

            <AdmicityDialog
                open={Boolean(rulePendingDelete)}
                handleClose={() => setRulePendingDelete(null)}
                title="Remove restriction"
                contentText={
                    rulePendingDelete
                        ? `Remove student profile access restriction for "${rulePendingDelete.subjectLabel}"?`
                        : ''
                }
                actions={[
                    {
                        label: 'Cancel',
                        onClick: () => setRulePendingDelete(null),
                        disabled: isDeleting
                    },
                    {
                        label: isDeleting ? 'Removing...' : 'Remove',
                        onClick: handleConfirmDelete,
                        disabled: isDeleting
                    }
                ]}
            />
        </SettingsSectionBase>
    );
};

export default StudentProfileAccessRestrictions;
